import { createSelector } from '@reduxjs/toolkit';
import { moduleInactiveHint, moduleInactiveLabel, wmsOrderStateLabel } from '../../utils/inventoryLabels';
import {
  FLOW_DISABLED_LABEL,
  FLOW_DISABLED_REASON,
  HOME_TAB,
  OPERATOR_CAPABILITY,
  OPERATOR_FLOW,
  OPERATOR_FLOW_ORDER,
  OPERATOR_FLOW_PATHS,
  OPERATOR_HIDDEN_FLOWS,
  OPERATOR_TASK_FLOWS,
  ORDER_FLOW_LABELS,
} from './operatorConstants';

/**
 * The warehouse to keep after the list comes back: the only one, or the one
 * remembered if it is still in reach. Otherwise none, and Inicio asks.
 *
 * @param {Array<{id: number}>} warehouses
 * @param {number|null} selectedId
 * @returns {number|null}
 */
export const resolveOperatorWarehouse = (warehouses, selectedId) => {
  if (warehouses.length === 1) return warehouses[0].id;
  return warehouses.some(({ id }) => id === selectedId) ? selectedId : null;
};

export const selectOperatorWarehouses = (state) => state.operator.warehouses;

export const selectCurrentOperatorWarehouseId = (state) => state.operator.selectedWarehouseId;

export const selectCurrentOperatorWarehouse = createSelector(
  [selectOperatorWarehouses, selectCurrentOperatorWarehouseId],
  (warehouses, id) => warehouses.find((warehouse) => warehouse.id === id) || null
);

/** Whether the backend serves picking by route: until it says so, the queue offers no batches. */
export const selectPickingRouteReady = (state) =>
  (state.operator.capabilities || []).includes(OPERATOR_CAPABILITY.PICKING_ROUTE);

/** Whether the backend reserves an order's stock from the floor: until it says so, the walk offers no «Reservar stock». */
export const selectOrderReserveReady = (state) =>
  (state.operator.capabilities || []).includes(OPERATOR_CAPABILITY.ORDER_RESERVE);

/** Whether the backend sends a packed order to its courier from the floor: until it says so, packing offers no «Enviar al courier». */
export const selectCourierSendReady = (state) =>
  (state.operator.capabilities || []).includes(OPERATOR_CAPABILITY.COURIER_SEND);

/**
 * Whether the backend creates the shipment again once the floor changed the items of a labeled order:
 * until it says so, packing says nothing of it.
 */
export const selectCourierRelabelReady = (state) =>
  (state.operator.capabilities || []).includes(OPERATOR_CAPABILITY.COURIER_RELABEL);

/** The flows the current warehouse lets this operator work, in floor order. */
export const selectEnabledFlows = createSelector([selectCurrentOperatorWarehouse], (warehouse) =>
  OPERATOR_FLOW_ORDER.filter((flow) => warehouse?.flows?.[flow] === true)
);

/**
 * The flows a warehouse shows the floor: those it enables, but the hidden ones.
 *
 * @param {{flows?: Object<string, boolean>}|null} warehouse
 * @param {string[]} [hidden]
 * @returns {string[]}
 */
export const shownFlows = (warehouse, hidden = OPERATOR_HIDDEN_FLOWS) =>
  OPERATOR_FLOW_ORDER.filter((flow) => warehouse?.flows?.[flow] === true && !hidden.includes(flow));

/** The current warehouse's flows as the floor sees them: Trabajo libre's cards. */
export const selectShownFlows = createSelector([selectCurrentOperatorWarehouse], (warehouse) => shownFlows(warehouse));

/**
 * The operator's choices on this device. A key missing from what was
 * persisted — added in a later version — reads as on, but the camera's
 * diagnostics, which read as off, and Inicio's tab as Tareas: the stored
 * `prefs` replace the initial ones whole.
 *
 * @returns {{sound: boolean, vibration: boolean, camera: boolean, cameraDiagnostics: boolean, homeTab: string}}
 */
export const selectOperatorPrefs = createSelector([(state) => state.operator.prefs], (prefs) => ({
  sound: prefs?.sound !== false,
  vibration: prefs?.vibration !== false,
  camera: prefs?.camera !== false,
  cameraDiagnostics: prefs?.cameraDiagnostics === true,
  homeTab: prefs?.homeTab ?? HOME_TAB.TASKS,
}));

/** Whether a flow open here makes tasks: only then is there a tray to ask for. */
export const selectHasTaskFlows = createSelector([selectEnabledFlows], (flows) =>
  flows.some((flow) => OPERATOR_TASK_FLOWS.includes(flow))
);

const HOME_TABS = [HOME_TAB.TASKS, HOME_TAB.FREE];
const NO_HOME_TABS = [];

/**
 * Inicio's tabs: Tareas and Trabajo libre, where there is a tray. None in
 * Lite, in a warehouse without flows, or where no flow makes tasks.
 */
export const selectHomeTabs = (state) => (selectHasTaskFlows(state) ? HOME_TABS : NO_HOME_TABS);

/**
 * The tab Inicio shows: the last one chosen on this phone, if Inicio has it,
 * and Tareas otherwise. `null` without tabs.
 */
export const selectHomeTab = (state) => {
  const tabs = selectHomeTabs(state);
  if (tabs.length === 0) return null;
  const { homeTab } = selectOperatorPrefs(state);
  return tabs.includes(homeTab) ? homeTab : HOME_TAB.TASKS;
};

/** iOS plays nothing until a tap unlocks the audio; until then the layout listens for one. */
export const selectNeedsAudioUnlock = (state) =>
  selectOperatorPrefs(state).sound && state.operatorDevice.audio === 'locked';

const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

/**
 * How big a flow's queue is, for its card on Inicio.
 *
 * @param {string} flow
 * @param {object|null} queues - `warehouse.queues`; null when the server did not count.
 * @returns {{count: number, caption: string}|null}
 */
export const queueSummary = (flow, queues) => {
  if (!queues) return null;

  switch (flow) {
    case OPERATOR_FLOW.RECEIVING: {
      const count = queues.receipts_open ?? 0;
      return { count, caption: plural(count, 'recepción abierta', 'recepciones abiertas') };
    }
    case OPERATOR_FLOW.PUTAWAY: {
      const rows = (queues.putaway_receiving?.rows ?? 0) + (queues.putaway_restock?.rows ?? 0);
      const units = (queues.putaway_receiving?.units ?? 0) + (queues.putaway_restock?.units ?? 0);
      return { count: rows, caption: `${plural(rows, 'fila', 'filas')} · ${units} un por guardar` };
    }
    case OPERATOR_FLOW.PICKING: {
      const count = queues.picking_orders ?? 0;
      return { count, caption: plural(count, 'orden por pickear', 'órdenes por pickear') };
    }
    case OPERATOR_FLOW.PACKING: {
      const count = queues.packing_orders ?? 0;
      return { count, caption: plural(count, 'orden por empacar', 'órdenes por empacar') };
    }
    case OPERATOR_FLOW.DISPATCH: {
      const count = queues.dispatch_orders ?? 0;
      return { count, caption: plural(count, 'orden por despachar', 'órdenes por despachar') };
    }
    default:
      return null;
  }
};

const reasonLabel = (reason) => (reason === FLOW_DISABLED_REASON ? FLOW_DISABLED_LABEL : moduleInactiveLabel(reason));

/**
 * Why the companies of a warehouse cannot be worked, one entry per company
 * with a reason. Three different things, fixed in three different places, so
 * saying only "no" would send people to look in the wrong one.
 *
 * @param {object|null} warehouse
 * @returns {Array<{name: string, label: string, hint: string}>}
 */
export const inactiveCompanies = (warehouse) =>
  (warehouse?.companies || [])
    .filter((company) => company.reason)
    .map((company) => ({
      name: company.name,
      label: reasonLabel(company.reason),
      hint: moduleInactiveHint(company.reason),
    }));

/** A warehouse whose only flow is Consulta: the Lite module. Kits, like every other flow, is Pro. */
export const isLookupOnly = (warehouse) =>
  Boolean(warehouse?.flows?.stock) &&
  OPERATOR_FLOW_ORDER.filter((flow) => flow !== OPERATOR_FLOW.STOCK).every((flow) => !warehouse.flows[flow]);

/** A scanned order as its screens title it: «ORD-1», or its number. */
export const scannedOrderName = (result) => result.reference_id || `Orden #${result.order_id}`;

/**
 * An order as its card and its header title it: its number and its reference,
 * «#60 - ORD-60», or «#60» alone. Sentences, sheets and the band keep naming
 * it by its reference.
 *
 * @param {{id?: number, order_id?: number, reference_id?: string|null}} order
 */
export const orderCardTitle = (order) => {
  const id = order?.id ?? order?.order_id;
  return order?.reference_id ? `#${id} - ${order.reference_id}` : `#${id}`;
};

const ORDER_OPEN_VERBS = {
  [OPERATOR_FLOW.PICKING]: 'Pickear',
  [OPERATOR_FLOW.PACKING]: 'Empacar',
  [OPERATOR_FLOW.DISPATCH]: 'Despachar',
};

/**
 * The router state of a detour: the screen it opens goes back to `to` when its
 * job is done, its primary saying `label` («Volver a Despacho»), instead of
 * going on through its own flow.
 *
 * @param {string} to
 * @param {string} label
 */
export const returnState = (to, label) => ({ returnTo: { to, label } });

/** The way back a screen was opened with (`returnState`); `null` for none or anything else in the router state. */
export const returnToOf = (state) => {
  const returnTo = state?.returnTo;
  return typeof returnTo?.to === 'string' && returnTo.to.startsWith('/') && typeof returnTo.label === 'string'
    ? returnTo
    : null;
};

/**
 * A scanned order packed with no label yet, on its way to Despacho: the server
 * calls it `labeled` only once it has a tracking number, and Despacho takes it
 * only with its labels.
 */
export const isPackedWithoutLabel = (result) =>
  result?.wms_state === 'packed' && result?.next_flow === OPERATOR_FLOW.DISPATCH;

/** An order's own packing screen: where it goes to its courier and where its labels are printed. */
const packingScreenOf = (result) => `${OPERATOR_FLOW_PATHS[OPERATOR_FLOW.PACKING]}/${result.order_id}`;

/**
 * The station a scanned order opens in, as a link: «Pickear», «Empacar» or
 * «Despachar»; `null` when it goes nowhere the operator works here. Despacho
 * opens its station: an order goes up to the load by scanning its items.
 * Packed with no label it cannot go up yet: «Generar etiqueta» opens its
 * packing screen, which sends it to its courier.
 *
 * @param {{order_id: number, wms_state?: string, next_flow: string|null}} result - A scan result of kind `order`.
 * @param {string[]} flows - The flows open in this warehouse.
 * @param {object} [options]
 * @param {boolean} [options.courierSend] - Whether the backend sends to the courier from the floor.
 * @param {object} [options.from] - The router state that brings «Generar etiqueta» back (`returnState`).
 */
export const orderOpenLink = (result, flows, { courierSend = false, from } = {}) => {
  if (isPackedWithoutLabel(result)) {
    if (!courierSend || !flows.includes(OPERATOR_FLOW.PACKING)) return null;
    return { to: packingScreenOf(result), label: 'Generar etiqueta', state: from };
  }
  const label = ORDER_OPEN_VERBS[result.next_flow];
  if (!label || !flows.includes(result.next_flow)) return null;
  const path = OPERATOR_FLOW_PATHS[result.next_flow];
  return { to: result.next_flow === OPERATOR_FLOW.DISPATCH ? path : `${path}/${result.order_id}`, label };
};

/**
 * A labeled order's labels, to print them again: its packing screen lists
 * them. `null` for any other order, or where packing is closed.
 *
 * @param {{order_id: number, wms_state?: string}} result - A scan result of kind `order`.
 * @param {string[]} flows
 * @param {{from?: object}} [options] - The router state that brings it back (`returnState`).
 */
export const orderLabelsLink = (result, flows, { from } = {}) =>
  result?.wms_state === 'labeled' && flows.includes(OPERATOR_FLOW.PACKING)
    ? { to: packingScreenOf(result), label: 'Ver etiquetas', state: from }
    : null;

/**
 * A scanned order the lists leave out while it waits for stock: nothing of it
 * picked yet, so it shows up in Picking once its stock is there. One already
 * started stays listed, marked, and opens as any other.
 *
 * @param {{waiting_stock?: boolean, next_flow?: string|null}|null} result
 */
export const isWaitingOffList = (result) => Boolean(result?.waiting_stock) && !result?.next_flow;

/** Where a scanned order goes now, without a link: «Va en Despacho». */
export const orderNextStepText = (result) => {
  if (isWaitingOffList(result)) return 'Espera stock: vuelve a Picking cuando llegue';
  if (isPackedWithoutLabel(result)) return 'Le falta la etiqueta: se genera en Empaque';
  return result.next_flow
    ? `Va en ${ORDER_FLOW_LABELS[result.next_flow] || result.next_flow}`
    : 'Sin nada pendiente en la bodega';
};

/**
 * Why a station does not open a scanned order, and where it goes:
 * «ORD-1 ya está pickeada: va en Empaque.»
 *
 * @param {{order_id: number, reference_id: string|null, wms_state: string, next_flow: string|null}} result
 */
export const orderElsewhereText = (result) => {
  const name = scannedOrderName(result);
  if (isWaitingOffList(result)) return `${name} espera stock: vuelve a Picking cuando llegue.`;
  if (isPackedWithoutLabel(result)) return `${name} ya está empacada, pero le falta la etiqueta para ir a Despacho.`;
  const state = String(wmsOrderStateLabel(result.wms_state) || '').toLowerCase();
  switch (result.next_flow) {
    case OPERATOR_FLOW.PICKING:
      return `${name} todavía tiene productos por pickear: va en Picking.`;
    case OPERATOR_FLOW.PACKING:
      return `${name} ya está pickeada: va en Empaque.`;
    case null:
    case undefined:
      return `${name} está ${state}: no tiene nada pendiente en la bodega.`;
    default:
      return `${name} ya está ${state}: va en ${ORDER_FLOW_LABELS[result.next_flow] || result.next_flow}.`;
  }
};

/** @param {object|null} profile - `me.operator`. */
export const operatorDisplayName = (profile) =>
  [profile?.first_name, profile?.last_name].filter(Boolean).join(' ') || profile?.email || 'Operario';

/** A message as a sentence, with its full stop; `''` for nothing. */
export const asSentence = (text) => {
  const value = String(text || '').trim();
  return value && !/[.!?]$/.test(value) ? `${value}.` : value;
};

// Quantities come with up to three decimals: summed in thousandths, floats do not invent a remainder.
const thousandths = (quantity) => Math.round(Number(quantity) * 1000);

/**
 * Whether a slot holds more than Consulta lists in it: its occupancy counts
 * every client's goods, and its contents only those of clients with the
 * module in this warehouse.
 *
 * @param {{slot: {occupancy?: number|string|null}, contents?: Array<{quantity: number|string}>}} slotStock
 */
export const slotHasUnlisted = ({ slot, contents = [] }) =>
  thousandths(slot?.occupancy ?? 0) > contents.reduce((sum, item) => sum + thousandths(item.quantity), 0);
